import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { inventoryService } from "../services";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const unwrapStockCountPayload = (response) => {
  const basePayload = response?.data ?? response ?? null;
  const nestedPayload = basePayload?.data && typeof basePayload.data === "object" ? basePayload.data : null;
  const payload = nestedPayload?.id ? nestedPayload : basePayload;
  if (!payload || typeof payload !== "object") {
    return null;
  }
  return {
    ...payload,
    items: toArray(payload?.items),
  };
};

const replaceCountItem = (countState, updatedItem) => {
  if (!countState) return countState;
  if (!updatedItem?.id) return countState;
  return {
    ...countState,
    items: toArray(countState.items).map((item) => (item.id === updatedItem.id ? updatedItem : item)),
  };
};

const mergeCountItemResponse = (countState, updatedItem) => {
  const nextState = replaceCountItem(countState, updatedItem);
  if (!nextState || !updatedItem || !Array.isArray(updatedItem.stock_count_crosses)) {
    return nextState;
  }
  return {
    ...nextState,
    crosses: toArray(updatedItem.stock_count_crosses),
  };
};

function useStockCount(countType, branchId) {
  const [searchParams, setSearchParams] = useSearchParams();
  const countIdParam = useMemo(() => {
    const raw = Number(searchParams.get("count_id"));
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  }, [searchParams]);

  const [countState, setCountState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterMode, setFilterMode] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const updateSearchParamsCountId = useCallback(
    (nextCountId) => {
      if (!nextCountId) return;
      setSearchParams({ count_id: String(nextCountId) }, { replace: true });
    },
    [setSearchParams]
  );

  const loadCountById = useCallback(
    async (countId) => {
      if (!branchId || !countId) return null;
      const response = await inventoryService.getStockCount(countId, { branch_id: branchId });
      return unwrapStockCountPayload(response);
    },
    [branchId]
  );

  const refreshCount = useCallback(async () => {
    if (!branchId || !countIdParam) {
      setCountState(null);
      setError("");
      return null;
    }

    setLoading(true);
    setError("");
    try {
      const payload = await loadCountById(countIdParam);
      setCountState(payload);
      return payload;
    } catch (requestError) {
      setCountState(null);
      setError(requestError?.response?.data?.detail || "No se pudo cargar el conteo de inventario.");
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, [branchId, countIdParam, loadCountById]);

  useEffect(() => {
    if (!branchId) {
      setCountState(null);
      setError("");
      setLoading(false);
      return;
    }

    if (!countIdParam) {
      setCountState(null);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const payload = await loadCountById(countIdParam);
        if (!cancelled) {
          setCountState(payload);
        }
      } catch (requestError) {
        if (!cancelled) {
          setCountState(null);
          setError(requestError?.response?.data?.detail || "No se pudo cargar el conteo de inventario.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [branchId, countIdParam, loadCountById]);

  const startCount = useCallback(
    async (payload = {}) => {
      if (!branchId) return null;
      setLoading(true);
      setError("");
      try {
        const response = await inventoryService.createStockCount({
          branch_id: branchId,
          count_type: countType,
          ...payload,
        });
        const nextPayload = unwrapStockCountPayload(response);
        setCountState(nextPayload);
        if (nextPayload?.id) {
          updateSearchParamsCountId(nextPayload.id);
        }
        return nextPayload;
      } catch (requestError) {
        setError(requestError?.response?.data?.detail || "No se pudo iniciar el conteo de inventario.");
        throw requestError;
      } finally {
        setLoading(false);
      }
    },
    [branchId, countType, updateSearchParamsCountId]
  );

  const clearCount = useCallback(() => {
    setCountState(null);
    setError("");
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const addToItem = useCallback(
    async (itemId, qtyToAdd) => {
      if (!branchId || !countState?.id || !itemId) return null;
      const response = await inventoryService.updateStockCountItem(countState.id, itemId, {
        branch_id: branchId,
        qty_to_add: qtyToAdd,
      });
      const updatedItem = response?.data ?? response ?? null;
      setCountState((prev) => mergeCountItemResponse(prev, updatedItem));
      setError("");
      return updatedItem;
    },
    [branchId, countState?.id]
  );

  const addEntry = useCallback(
    async (itemId, payload = {}) => {
      if (!branchId || !countState?.id || !itemId) return null;
      const response = await inventoryService.createStockCountEntry(countState.id, itemId, {
        branch_id: branchId,
        ...payload,
      });
      const updatedItem = response?.data ?? response ?? null;
      setCountState((prev) => mergeCountItemResponse(prev, updatedItem));
      setError("");
      return updatedItem;
    },
    [branchId, countState?.id]
  );

  const updateEntry = useCallback(
    async (itemId, entryId, payload = {}) => {
      if (!branchId || !countState?.id || !itemId || !entryId) return null;
      const response = await inventoryService.updateStockCountEntry(countState.id, itemId, entryId, {
        branch_id: branchId,
        ...payload,
      });
      const updatedItem = response?.data ?? response ?? null;
      setCountState((prev) => mergeCountItemResponse(prev, updatedItem));
      setError("");
      return updatedItem;
    },
    [branchId, countState?.id]
  );

  const voidEntry = useCallback(
    async (itemId, entryId, payload = {}) => {
      if (!branchId || !countState?.id || !itemId || !entryId) return null;
      const response = await inventoryService.voidStockCountEntry(countState.id, itemId, entryId, {
        branch_id: branchId,
        ...payload,
      });
      const updatedItem = response?.data ?? response ?? null;
      setCountState((prev) => mergeCountItemResponse(prev, updatedItem));
      setError("");
      return updatedItem;
    },
    [branchId, countState?.id]
  );

  const resetItem = useCallback(
    async (itemId) => {
      if (!branchId || !countState?.id || !itemId) return null;
      const response = await inventoryService.resetStockCountItem(countState.id, itemId, {
        branch_id: branchId,
      });
      const updatedItem = response?.data ?? response ?? null;
      setCountState((prev) => mergeCountItemResponse(prev, updatedItem));
      setError("");
      return updatedItem;
    },
    [branchId, countState?.id]
  );

  const deleteProduct = useCallback(
    async (itemId) => {
      if (!branchId || !countState?.id || !itemId) return null;
      const response = await inventoryService.deleteStockCountItem(countState.id, itemId, {
        branch_id: branchId,
      });
      const payload = unwrapStockCountPayload(response);
      setCountState(payload);
      setError("");
      return payload;
    },
    [branchId, countState?.id]
  );

  const applyCount = useCallback(
    async (note) => {
      if (!branchId || !countState?.id) return null;
      const response = await inventoryService.applyStockCount(countState.id, {
        branch_id: branchId,
        comment: note,
      });
      const payload = unwrapStockCountPayload(response);
      setCountState(payload);
      setError("");
      return payload;
    },
    [branchId, countState?.id]
  );

  const submitCount = useCallback(async () => {
    if (!branchId || !countState?.id) return null;
    const response = await inventoryService.submitStockCount(countState.id, {
      branch_id: branchId,
    });
    const payload = unwrapStockCountPayload(response);
    setCountState(payload);
    setError("");
    return payload;
  }, [branchId, countState?.id]);

  const items = useMemo(() => toArray(countState?.items), [countState?.items]);
  const groups = useMemo(() => toArray(countState?.groups), [countState?.groups]);
  const crosses = useMemo(() => toArray(countState?.crosses), [countState?.crosses]);
  const selectiveNotes = useMemo(() => toArray(countState?.selective_notes), [countState?.selective_notes]);
  const isReadOnly =
    countState?.status === "APPLIED" ||
    countState?.status === "CLOSED" ||
    countState?.status === "PENDING_REVIEW" ||
    countState?.status === "REVIEWING" ||
    countState?.status === "CANCELED";

  const filteredItems = useMemo(() => {
    const normalizedQuery = String(searchQuery || "").trim().toLowerCase();
    return items.filter((item) => {
      if (filterMode === "COUNTED" && !item?.is_counted) return false;
      if (filterMode === "PENDING" && item?.is_counted) return false;

      if (!normalizedQuery) return true;
      const haystack = [
        item?.product_presentation?.product?.description,
        item?.product_presentation?.product?.code,
        item?.product_presentation?.product_format?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [filterMode, items, searchQuery]);

  return {
    items,
    groups,
    crosses,
    selectiveNotes,
    loading,
    error,
    countId: countState?.id || countIdParam || null,
    countStatus: countState?.status || null,
    filterMode,
    setFilterMode,
    searchQuery,
    setSearchQuery,
    filteredItems,
    addToItem,
    addEntry,
    updateEntry,
    voidEntry,
    resetItem,
    deleteProduct,
    applyCount,
    submitCount,
    startCount,
    clearCount,
    refreshCount,
    countAppliedAt: countState?.applied_at || null,
    countSubmittedAt: countState?.submitted_at || null,
    countGroups: countState?.count_groups || "",
    settingsSnapshot: countState?.settings_snapshot || {},
    isReadOnly,
    count: countState,
  };
}

export default useStockCount;
